import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSseSubscription } from '../context/SseContext';
import NavHeader from '../components/NavHeader';
import ChecklistProgressBar from '../components/ChecklistProgressBar';
import RequestorApproverModal from '../components/RequestorApproverModal';
import {
  fetchVatDashboardProjection,
  fetchPayingBanks,
  fetchCompanyCodes,
  generateVatBatch,
  getVatBatch,
  getVatFxOptions,
  getVatFxOptionsPreBatch,
  downloadVatBatch,
  regenerateVatBatch,
  submitVatBatch,
  getVatBatchChecklist,
  toggleVatBatchChecklistItem,
} from '../api';

const DEFAULT_REQUESTOR = 'Sandra Bantic - Senior Tax Analyst';

const ALL_PERIODS = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8', 'P9', 'P10', 'P11', 'P12'];

function formatCurrency(amount, currency) {
  if (amount == null || amount === '') return '';
  const num = parseFloat(amount);
  if (isNaN(num)) return '';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency || ''}`.trim();
  }
}

function BatchStatusBadge({ status }) {
  if (!status) return <span className="vat-status vat-status-open">Open</span>;
  const labels = { draft: 'Draft', packaged: 'Packaged', submitted: 'Submitted', paid: 'Paid' };
  return <span className={`vat-status vat-status-${status}`}>{labels[status] || status}</span>;
}

function VatDashboard() {
  const navigate = useNavigate();

  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [lastHash, setLastHash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [periodFilter, setPeriodFilter] = useState('all');
  const [includeSubmitted, setIncludeSubmitted] = useState(false);
  const [collapsedPeriods, setCollapsedPeriods] = useState(() => new Set());

  const [busyBatch, setBusyBatch] = useState(null);
  const [toast, setToast] = useState(null);

  // Requestor/approver cover-sheet modal (P0 item 3). `mode` is either
  // 'generate' (a new package — sends the names to generateVatBatch) or
  // 'regenerate' (an existing package — prefilled from the batch; edited
  // names are sent along so the rebuilt package picks them up).
  const [packageModal, setPackageModal] = useState(null);
  const [modalRequestor, setModalRequestor] = useState('');
  const [modalApprover, setModalApprover] = useState('');
  const [modalBusy, setModalBusy] = useState(false);
  // FX-rate-picker options for the modal. Fetched on demand when regenerating
  // an existing batch (a batch id is required to resolve the rates). A fresh
  // package has no batch yet, so the picker is simply hidden there.
  const [modalFxOptions, setModalFxOptions] = useState(null);
  // Active paying banks for the "Transfer funds from" select (fetched when
  // the modal opens; null hides the select) and the preselected id (the
  // batch's stored paying_bank_account_id on regenerate, '' otherwise).
  // Left null on purpose when per-invoice packages are on — see
  // `perInvoicePackages` below.
  const [modalPayingBanks, setModalPayingBanks] = useState(null);
  const [modalPayingBankId, setModalPayingBankId] = useState('');
  // When a regenerate's stored paying bank is NOT in the active list
  // (deactivated or deleted), it is surfaced as a synthetic option and this
  // holds its id: confirming with it still selected OMITS the field so the
  // backend keeps the batch's existing snapshot (re-sending a deleted id
  // would 400; sending null would silently wipe the snapshot).
  const [modalKeepBankId, setModalKeepBankId] = useState(null);
  // Active company codes for the JE "Company code" select (fetched when the
  // modal opens; null hides the select) and the preselected id (the batch's
  // stored company_code_id on regenerate, '' otherwise).
  const [modalCompanyCodes, setModalCompanyCodes] = useState(null);
  const [modalCompanyCodeId, setModalCompanyCodeId] = useState('');
  // Same keep-omits-field pattern as modalKeepBankId: when a regenerate's
  // stored company code is no longer in the active list, it is surfaced as
  // a synthetic option and confirming with it still selected OMITS the
  // field so the backend keeps the batch's existing snapshot.
  const [modalKeepCompanyCodeId, setModalKeepCompanyCodeId] = useState(null);

  // `vat_per_invoice_packages_enabled`, as announced by the VAT dashboard
  // projection itself (`payload.per_invoice_packages`). When it is on a VAT
  // batch holds exactly one invoice, so:
  //
  // - the action controls live on each INVOICE row rather than on the vendor
  //   subtotal row (the projection puts `batch` on the invoice and leaves
  //   `vendor.batch` null to match);
  // - the generated package drops page 1 (the EFT wire request form), the
  //   only thing that ever rendered the paying bank, so the "Transfer funds
  //   from" select is hidden in both modal modes. The company code select,
  //   the FX picker and requestor/approver are untouched.
  //
  // Deliberately NOT read from GET /settings: that endpoint is restricted to
  // processor/admin while /vat is open to every authenticated user, so an
  // approver's fetch 403s and the page would silently render the grouped UI
  // over per-invoice data. Taking it from the payload the rows came from
  // makes the two impossible to disagree.
  const [perInvoicePackages, setPerInvoicePackages] = useState(false);

  // Per-batch expand toggle for the inline checklist + the live items.
  // Items are loaded on demand the first time a batch row is expanded; the
  // projection's `checklist_progress` summary keeps the bar in sync until
  // we have the detail.
  const [expandedBatches, setExpandedBatches] = useState(() => new Set());
  const [batchChecklistItems, setBatchChecklistItems] = useState({});
  const [checklistBusyItem, setChecklistBusyItem] = useState(null);

  const loadBatchChecklist = useCallback(async (batchId) => {
    try {
      const res = await getVatBatchChecklist(batchId);
      setBatchChecklistItems((prev) => ({ ...prev, [batchId]: res.data || [] }));
    } catch {
      // Leave the items unset — the row will show a brief loading state.
    }
  }, []);

  const toggleBatchExpanded = (batchId) => {
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
        if (!batchChecklistItems[batchId]) {
          loadBatchChecklist(batchId);
        }
      }
      return next;
    });
  };

  const handleToggleChecklistItem = async (batchId, item) => {
    setChecklistBusyItem(item.id);
    // Optimistic update so the bar/checkbox flip immediately.
    setBatchChecklistItems((prev) => {
      const list = prev[batchId] || [];
      return {
        ...prev,
        [batchId]: list.map((it) =>
          it.id === item.id ? { ...it, completed: !item.completed } : it
        ),
      };
    });
    try {
      await toggleVatBatchChecklistItem(batchId, item.id, !item.completed);
      // Re-pull the projection so the progress bar (driven by the projection)
      // and any downstream gates pick up the change.
      loadData();
      loadBatchChecklist(batchId);
    } catch (err) {
      // Revert on failure.
      setBatchChecklistItems((prev) => {
        const list = prev[batchId] || [];
        return {
          ...prev,
          [batchId]: list.map((it) =>
            it.id === item.id ? { ...it, completed: item.completed } : it
          ),
        };
      });
      showToast(err.response?.data?.detail || 'Failed to update checklist', 'error');
    } finally {
      setChecklistBusyItem(null);
    }
  };

  const togglePeriodCollapsed = (periodName) => {
    setCollapsedPeriods((prev) => {
      const next = new Set(prev);
      if (next.has(periodName)) next.delete(periodName);
      else next.add(periodName);
      return next;
    });
  };

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const res = await fetchVatDashboardProjection();
      const data = res.data;
      if (data.hash && data.hash === lastHash) return;
      setLastHash(data.hash);
      const fys = data.payload?.fiscal_years ?? [];
      setFiscalYears(fys);
      // The mode rides on the payload (see the state declaration above), so
      // it always matches the row shapes rendered from the same response.
      setPerInvoicePackages(!!data.payload?.per_invoice_packages);
      // Default the FY dropdown to the most recent FY in the payload (the
      // backend already sorts these descending, so head is newest). Only
      // overwrite the user's selection on the first load.
      setSelectedFiscalYear((current) => {
        if (current != null && fys.some((fy) => fy.fiscal_year === current)) {
          return current;
        }
        return fys[0]?.fiscal_year ?? null;
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load VAT dashboard');
    } finally {
      setLoading(false);
    }
  }, [lastHash]);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE — refresh via the shared connection (loadData early-returns on an
  // unchanged hash, so redundant messages are cheap).
  useSseSubscription(() => loadData());

  const selectedFyEntry = useMemo(
    () => fiscalYears.find((fy) => fy.fiscal_year === selectedFiscalYear) || null,
    [fiscalYears, selectedFiscalYear]
  );

  const visiblePeriods = useMemo(() => {
    if (!selectedFyEntry) return [];
    let out = selectedFyEntry.periods || [];
    if (periodFilter !== 'all') {
      out = out.filter((p) => p.period === periodFilter);
    }
    if (!includeSubmitted) {
      // Grouped mode: one batch per vendor, so a submitted batch hides the
      // whole vendor group. Per-invoice mode: `vendor.batch` is null by
      // design, so the test moves to the invoice rows — and a vendor is only
      // hidden once EVERY one of its packages is submitted. Hiding individual
      // rows instead would leave the vendor's precomputed subtotals
      // describing invoices that are no longer on screen.
      const isSubmitted = (vendor) =>
        perInvoicePackages
          ? vendor.invoices.length > 0 &&
            vendor.invoices.every((inv) => inv.batch && inv.batch.status === 'submitted')
          : !!(vendor.batch && vendor.batch.status === 'submitted');
      out = out
        .map((p) => ({
          ...p,
          vendors: p.vendors.filter((v) => !isSubmitted(v)),
        }))
        .filter((p) => p.vendors.length > 0);
    }
    return out;
  }, [selectedFyEntry, periodFilter, includeSubmitted, perInvoicePackages]);

  // The "to-date" strip beneath the dropdowns: when a specific period is
  // selected, show that period's running totals through P{n}; otherwise
  // show the entire FY's totals.
  const toDateTotals = useMemo(() => {
    if (!selectedFyEntry) return null;
    if (periodFilter !== 'all') {
      const p = selectedFyEntry.periods.find((x) => x.period === periodFilter);
      if (!p) return null;
      return {
        scope: `${periodFilter} of FY ${selectedFiscalYear} (period-to-date)`,
        currency: p.currency,
        service: p.ytd_total_service,
        vat: p.ytd_total_vat,
        invoice_total: p.ytd_total_invoice_total,
      };
    }
    const t = selectedFyEntry.totals || {};
    return {
      scope: `FY ${selectedFiscalYear} to date`,
      currency: t.currency || selectedFyEntry.currency,
      service: t.total_service,
      vat: t.total_vat,
      invoice_total: t.total_invoice_total,
    };
  }, [selectedFyEntry, periodFilter, selectedFiscalYear]);

  // Open the requestor/approver modal for a fresh package. Requestor defaults
  // to Sandra; approver is left blank (backend resolves VAT_APPROVER_ID).
  // Load the active paying banks for the modal's "Transfer funds from"
  // select. Returns the list (also stored in state) or null on failure — a
  // null list hides the select and the batch's stored snapshot is left
  // untouched on regenerate.
  const loadModalPayingBanks = async () => {
    try {
      const res = await fetchPayingBanks(true);
      const list = Array.isArray(res.data) ? res.data : [];
      setModalPayingBanks(list);
      return list;
    } catch {
      setModalPayingBanks(null);
      return null;
    }
  };

  // Same contract for the JE "Company code" select: returns the active list
  // (also stored in state) or null on failure — a null list hides the select
  // and the batch's stored snapshot is left untouched on regenerate.
  const loadModalCompanyCodes = async () => {
    try {
      const res = await fetchCompanyCodes(true);
      const list = Array.isArray(res.data) ? res.data : [];
      setModalCompanyCodes(list);
      return list;
    } catch {
      setModalCompanyCodes(null);
      return null;
    }
  };

  // `batchId` is the draft batch this generate finalizes. Sent from the
  // per-invoice rows so the backend scopes eligibility, totals and the FX
  // default to that one package; omitted (null) from the vendor subtotal
  // button, which keeps the historical vendor + period scope.
  const openGenerateModal = async (period, vendorId, batchId = null) => {
    if (selectedFiscalYear == null) {
      showToast('Pick a fiscal year first', 'error');
      return;
    }
    setModalRequestor(DEFAULT_REQUESTOR);
    setModalApprover('');
    // A fresh package has no batch yet, but the pre-batch fx-options endpoint
    // computes the prospective currency + rates from the eligible invoices so
    // the FX picker shows on first generate. `current_override` is null there.
    setModalFxOptions(null);
    setModalPayingBanks(null);
    setModalPayingBankId('');
    setModalKeepBankId(null);
    setModalCompanyCodes(null);
    setModalCompanyCodeId('');
    setModalKeepCompanyCodeId(null);
    setPackageModal({ mode: 'generate', period, vendorId, batchId });
    // Per-invoice packages: skip the paying-bank load so the list stays null
    // and the select stays hidden. Confirm then sends the field as null,
    // which is exactly what a hidden/"— none —" select already did — and a
    // fresh batch has no snapshot to preserve either way.
    if (!perInvoicePackages) {
      loadModalPayingBanks();
    }
    loadModalCompanyCodes();
    try {
      const fx = await getVatFxOptionsPreBatch(
        period,
        vendorId,
        selectedFiscalYear,
        batchId
      );
      setModalFxOptions(fx.data || null);
    } catch {
      // No picker if the rates can't be resolved — generate still works with
      // the dynamic default rate.
    }
  };

  // Open the modal for a regenerate, prefilled from the batch's stored
  // requestor/approver (fetched on demand so the projection stays lean). Also
  // pulls the FX-rate options so the user can review/override the rate the
  // regenerated package uses.
  const openRegenerateModal = async (batchId) => {
    setModalRequestor(DEFAULT_REQUESTOR);
    setModalApprover('');
    setModalFxOptions(null);
    setModalPayingBanks(null);
    setModalPayingBankId('');
    setModalKeepBankId(null);
    setModalCompanyCodes(null);
    setModalCompanyCodeId('');
    setModalKeepCompanyCodeId(null);
    setPackageModal({ mode: 'regenerate', batchId });
    // allSettled (not all): if the batch fetch fails we must know whether the
    // bank/code lists still loaded — showing a select without knowing the
    // batch's stored value would send an explicit null on confirm and
    // silently wipe the snapshot, so in that case the selects are hidden
    // (fields omitted).
    const [batchRes, banksRes, codesRes] = await Promise.allSettled([
      getVatBatch(batchId),
      // Per-invoice packages: skip the paying-bank load. Resolving to null
      // (rather than dropping the entry) keeps the destructuring and the
      // stored-bank branch below reading identically in both flag states —
      // `banks` is null, so nothing preselects, the select stays hidden, and
      // confirm OMITS paying_bank_account_id. Omitting matters: a batch
      // packaged before the flag was flipped keeps its stored snapshot
      // instead of having it silently cleared.
      perInvoicePackages ? Promise.resolve(null) : loadModalPayingBanks(),
      loadModalCompanyCodes(),
    ]);
    if (batchRes.status === 'fulfilled') {
      const data = batchRes.value.data || {};
      setModalRequestor(data.requestor_name || DEFAULT_REQUESTOR);
      setModalApprover(data.approver_name || '');
      // Preselect the batch's stored paying bank (spec 8b). When the stored
      // bank is no longer in the active list (deactivated or deleted), it is
      // still preselected via a synthetic option built from the batch's own
      // snapshot; confirming with it untouched omits the field so the
      // backend keeps the existing snapshot as-is.
      const banks =
        banksRes.status === 'fulfilled' ? banksRes.value : null;
      const storedId = data.paying_bank_account_id;
      if (storedId && banks) {
        if (banks.some((b) => b.id === storedId)) {
          setModalPayingBankId(storedId);
        } else {
          const snap = data.paying_bank_snapshot || {};
          setModalPayingBanks([
            {
              id: storedId,
              entity_name: `${snap.entity_name || 'Current bank'} (no longer active — kept as-is)`,
              bank_name: snap.bank_name || null,
              company_code: snap.company_code || null,
            },
            ...banks,
          ]);
          setModalPayingBankId(storedId);
          setModalKeepBankId(storedId);
        }
      }
      // Preselect the batch's stored company code — same synthetic-option
      // fallback as the paying bank when the stored row was deactivated or
      // deleted: confirming with it untouched omits the field so the
      // backend keeps the existing snapshot (never silently clears it).
      const codes =
        codesRes.status === 'fulfilled' ? codesRes.value : null;
      const storedCodeId = data.company_code_id;
      if (storedCodeId && codes) {
        if (codes.some((c) => c.id === storedCodeId)) {
          setModalCompanyCodeId(storedCodeId);
        } else {
          const snap = data.company_code_snapshot || {};
          setModalCompanyCodes([
            {
              id: storedCodeId,
              code: snap.code || '?',
              entity_name: `${snap.entity_name || 'Current company code'} (no longer active — kept as-is)`,
            },
            ...codes,
          ]);
          setModalCompanyCodeId(storedCodeId);
          setModalKeepCompanyCodeId(storedCodeId);
        }
      }
    } else {
      // Batch fetch failed: hide the selects so confirm omits the
      // paying-bank and company-code fields entirely and the batch's stored
      // snapshots are left untouched.
      setModalPayingBanks(null);
      setModalCompanyCodes(null);
    }
    try {
      const fx = await getVatFxOptions(batchId);
      setModalFxOptions(fx.data || null);
    } catch {
      // No picker if the rates can't be resolved — regenerate still works.
    }
  };

  const closePackageModal = () => {
    if (modalBusy) return;
    setPackageModal(null);
    setModalFxOptions(null);
    setModalPayingBanks(null);
    setModalPayingBankId('');
    setModalKeepBankId(null);
    setModalCompanyCodes(null);
    setModalCompanyCodeId('');
    setModalKeepCompanyCodeId(null);
  };

  const handleConfirmPackage = async (requestorName, approverName, fx, payingBankId, companyCodeId) => {
    if (!packageModal) return;
    const requestor = (requestorName || '').trim();
    const approver = (approverName || '').trim();
    const fxRate = fx ? fx.fx_rate : null;
    const fxRateDate = fx ? fx.fx_rate_date : null;
    setModalBusy(true);
    try {
      if (packageModal.mode === 'generate') {
        await generateVatBatch(
          packageModal.period,
          packageModal.vendorId,
          selectedFiscalYear,
          requestor || null,
          approver || null,
          fxRate,
          fxRateDate,
          // "Transfer funds from" selection ("— none —" / hidden select →
          // null → the EFT section renders blank).
          payingBankId || null,
          // JE "Company code" selection ("— default (1256) —" / hidden
          // select → null → the JE renders the hardcoded 1256).
          companyCodeId || null,
          // Target draft batch in per-invoice mode; null keeps the vendor +
          // period scope.
          packageModal.batchId || null
        );
        showToast('VAT package generation queued', 'success');
      } else {
        // Regenerate sends the (possibly edited) requestor/approver from the
        // modal — blank resets to the default — plus the chosen FX rate (when
        // changed from the default) so both drive the new package.
        // paying bank is presence-sensitive: undefined (select hidden — e.g.
        // the bank list failed to load — or the stored-but-inactive bank is
        // still selected) omits the field and keeps the batch's stored
        // snapshot; null clears it; a UUID re-snapshots.
        const keepStored =
          modalKeepBankId != null && payingBankId === modalKeepBankId;
        // Company code follows the same presence-sensitive rules, with its
        // own keep-omits-field guard for a stored-but-inactive code.
        const keepStoredCode =
          modalKeepCompanyCodeId != null && companyCodeId === modalKeepCompanyCodeId;
        await regenerateVatBatch(
          packageModal.batchId,
          requestor,
          approver,
          fxRate,
          fxRateDate,
          payingBankId === undefined || keepStored
            ? undefined
            : (payingBankId || null),
          companyCodeId === undefined || keepStoredCode
            ? undefined
            : (companyCodeId || null)
        );
        showToast('Package regenerating — refresh in a moment', 'success');
      }
      setPackageModal(null);
      setModalFxOptions(null);
      setModalPayingBanks(null);
      setModalPayingBankId('');
      setModalKeepBankId(null);
      setModalCompanyCodes(null);
      setModalCompanyCodeId('');
      setModalKeepCompanyCodeId(null);
      loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to generate package', 'error');
    } finally {
      setModalBusy(false);
    }
  };

  const handleDownload = async (batchId) => {
    setBusyBatch(batchId);
    try {
      const res = await downloadVatBatch(batchId);
      const url = res.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        showToast('No download URL returned', 'error');
      }
    } catch (err) {
      showToast(err.response?.data?.detail || 'Download failed', 'error');
    } finally {
      setBusyBatch(null);
    }
  };

  const handleSubmit = async (batchId) => {
    if (!window.confirm('Mark this VAT batch as Sent to AP? All invoices will transition to vat_submitted.')) {
      return;
    }
    setBusyBatch(batchId);
    try {
      await submitVatBatch(batchId);
      showToast('Batch sent to AP', 'success');
      loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to mark as Sent to AP', 'error');
    } finally {
      setBusyBatch(null);
    }
  };

  // ----- batch cell renderers -----
  //
  // The batch surface (status badge + checklist, and the generate / download /
  // regenerate / mark-as-sent controls) attaches to the VENDOR SUBTOTAL row in
  // grouped mode and to each INVOICE row in per-invoice mode, where a vendor +
  // period holds one batch per invoice. The projection emits the same batch
  // payload shape in both places, so both surfaces render through these
  // renderers rather than through two copies that could drift.

  const renderBatchStatusCell = (batch) => (
    <div className="vat-batch-status-cell">
      <BatchStatusBadge status={batch?.status} />
      {batch && (
        <>
          <button
            type="button"
            className="checklist-expand-toggle"
            onClick={() => toggleBatchExpanded(batch.id)}
            aria-expanded={expandedBatches.has(batch.id)}
            title={expandedBatches.has(batch.id) ? 'Hide checklist' : 'Show checklist'}
          >
            {expandedBatches.has(batch.id) ? '▾' : '▸'} Checklist
          </button>
          <ChecklistProgressBar progress={batch.checklist_progress} />
        </>
      )}
    </div>
  );

  const renderBatchActions = (batch, { canGenerate, onGenerate, generateTitle }) => {
    const downloading = batch && busyBatch === batch.id;
    return (
      <>
        {(!batch || (batch.status === 'draft' && !batch.package_s3_key)) && (
          <button
            className="btn btn-primary"
            disabled={!canGenerate}
            onClick={onGenerate}
            title={generateTitle}
          >
            Generate VAT Package
          </button>
        )}
        {batch && batch.package_s3_key && (
          <button
            className="btn btn-primary"
            disabled={downloading}
            onClick={() => handleDownload(batch.id)}
          >
            {downloading ? 'Opening...' : 'Download Package'}
          </button>
        )}
        {batch && batch.package_s3_key && (
          <button
            className="btn btn-secondary"
            style={{ marginLeft: 8 }}
            disabled={downloading}
            onClick={() => openRegenerateModal(batch.id)}
            title="Force-regenerate the cover pages and merged PDF"
          >
            Regenerate
          </button>
        )}
        {batch && batch.status === 'packaged' && (() => {
          const cp = batch.checklist_progress;
          const incomplete = !cp || cp.items_completed < cp.items_total;
          return (
            <button
              className="btn btn-secondary"
              style={{ marginLeft: 8 }}
              disabled={downloading || incomplete}
              onClick={() => handleSubmit(batch.id)}
              title={incomplete ? 'Complete the checklist before submitting' : 'Mark this VAT batch as Sent to AP'}
            >
              Mark as Sent to AP
            </button>
          );
        })()}
      </>
    );
  };

  // The expanded checklist panel row. In per-invoice mode several of these can
  // be open under one vendor, so they are ALL rendered after the subtotal row
  // rather than beneath their own invoice row — the vendor name cell spans the
  // invoice rows with rowSpan, and interleaving extra rows inside that span
  // would shift every column below it.
  const renderChecklistRow = (batch) => (
    <tr className="vat-checklist-expand-row" key={`checklist-${batch.id}`}>
      <td colSpan={9}>
        <div className="vat-checklist-panel">
          <div className="vat-checklist-panel-title">
            Submission package checklist
          </div>
          {batchChecklistItems[batch.id] ? (
            <ul className="batch-checklist-list">
              {batchChecklistItems[batch.id].map((item) => (
                <li key={item.id}>
                  <label className="batch-checklist-row">
                    <input
                      type="checkbox"
                      checked={!!item.completed}
                      disabled={checklistBusyItem === item.id}
                      onChange={() => handleToggleChecklistItem(batch.id, item)}
                    />
                    <span className="batch-checklist-label">{item.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          ) : (
            <div className="batch-checklist-loading">Loading checklist…</div>
          )}
        </div>
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div className="board-loading">
        <div className="loading-spinner">Loading VAT dashboard...</div>
      </div>
    );
  }

  return (
    <div className="kanban-page">
      <NavHeader />

      {toast && (
        <div className={`toast toast-${toast.type}`}>{toast.message}</div>
      )}

      <div className="vat-controls">
        <div className="vat-controls-left">
          <label className="vat-control-label">Fiscal Year</label>
          <select
            value={selectedFiscalYear ?? ''}
            onChange={(e) => setSelectedFiscalYear(Number(e.target.value))}
            className="status-select"
            disabled={fiscalYears.length === 0}
          >
            {fiscalYears.length === 0 && <option value="">—</option>}
            {fiscalYears.map((fy) => (
              <option key={fy.fiscal_year} value={fy.fiscal_year}>
                FY {fy.fiscal_year}
              </option>
            ))}
          </select>
          <label className="vat-control-label">Period</label>
          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            className="status-select"
          >
            <option value="all">All Periods</option>
            {ALL_PERIODS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <label className="vat-toggle">
          <input
            type="checkbox"
            checked={includeSubmitted}
            onChange={(e) => setIncludeSubmitted(e.target.checked)}
          />
          Include submitted batches
        </label>
      </div>

      {toDateTotals && (
        <div className="vat-todate-strip">
          <span className="vat-todate-scope">{toDateTotals.scope}</span>
          <span className="vat-todate-item">
            Service: <strong className="num">{formatCurrency(toDateTotals.service, toDateTotals.currency)}</strong>
          </span>
          <span className="vat-todate-item">
            VAT: <strong className="num">{formatCurrency(toDateTotals.vat, toDateTotals.currency)}</strong>
          </span>
          <span className="vat-todate-item">
            Invoice total: <strong className="num">{formatCurrency(toDateTotals.invoice_total, toDateTotals.currency)}</strong>
          </span>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {visiblePeriods.length === 0 ? (
        <div className="vat-empty">
          No VAT invoices match the current filters.
        </div>
      ) : (
        visiblePeriods.map((period) => {
          const collapsed = collapsedPeriods.has(period.period);
          return (
          <div className="vat-period" key={period.period}>
            <div className="vat-period-header">
              <button
                type="button"
                className="vat-period-toggle"
                onClick={() => togglePeriodCollapsed(period.period)}
                aria-expanded={!collapsed}
                aria-label={collapsed ? `Expand ${period.period}` : `Collapse ${period.period}`}
                title={collapsed ? 'Expand period' : 'Collapse period'}
              >
                <span className={`vat-period-caret${collapsed ? ' is-collapsed' : ''}`}>▾</span>
                <h2 className="vat-period-title">
                  {period.period} — FY {period.fiscal_year}
                  {period.period_start && period.period_end && (
                    <span className="vat-period-dates">
                      {' '}({period.period_start} to {period.period_end})
                    </span>
                  )}
                </h2>
              </button>
              <div className="vat-period-totals">
                <span className="vat-period-total-item">
                  Service: <strong className="num">{formatCurrency(period.total_service, period.currency)}</strong>
                </span>
                <span className="vat-period-total-item">
                  VAT: <strong className="num">{formatCurrency(period.total_vat, period.currency)}</strong>
                </span>
                <span className="vat-period-total-item">
                  Invoice total: <strong className="num">{formatCurrency(period.total_invoice_total, period.currency)}</strong>
                </span>
              </div>
            </div>

            {!collapsed && (
            <table className="vat-table">
              <colgroup>
                <col className="vat-col-vendor" />
                <col className="vat-col-date" />
                <col className="vat-col-invnum" />
                <col className="vat-col-amount" />
                <col className="vat-col-amount" />
                <col className="vat-col-amount" />
                <col className="vat-col-currency" />
                <col className="vat-col-status" />
                <col className="vat-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Invoice Date</th>
                  <th>Invoice #</th>
                  <th style={{ textAlign: 'right' }}>Service Amount</th>
                  <th style={{ textAlign: 'right' }}>VAT Amount</th>
                  <th style={{ textAlign: 'right' }}>Invoice Total</th>
                  <th>Currency</th>
                  <th>Batch Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {period.vendors.map((vendor) => {
                  const hasOpen = vendor.invoices.some((inv) => inv.status === 'vat_open');
                  // Null by design in per-invoice mode: the group has no single
                  // batch, so every control moves onto the invoice rows below.
                  const batch = vendor.batch;
                  return (
                    <React.Fragment key={vendor.vendor_id || vendor.vendor_name}>
                      {vendor.invoices.map((inv, idx) => (
                        <tr key={inv.invoice_id}>
                          {idx === 0 ? (
                            <td
                              rowSpan={vendor.invoices.length}
                              className="vat-vendor-cell"
                            >
                              <div className="vat-vendor-name">{vendor.vendor_name || '—'}</div>
                              {vendor.bank_account && (
                                <div className="vat-vendor-bank">
                                  {vendor.bank_account.bank_name}
                                  {vendor.bank_account.bank_location && ` · ${vendor.bank_account.bank_location}`}
                                  {vendor.bank_account.iban && (
                                    <div className="vat-bank-iban">IBAN: {vendor.bank_account.iban}</div>
                                  )}
                                </div>
                              )}
                            </td>
                          ) : null}
                          <td>{inv.invoice_date || '—'}</td>
                          <td>
                            <button
                              className="btn-link"
                              onClick={() => navigate(`/invoice/${inv.invoice_id}`)}
                              title="Open invoice detail"
                            >
                              {inv.invoice_number || inv.invoice_id.slice(0, 8)}
                            </button>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {formatCurrency(inv.service_amount, inv.currency)}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {formatCurrency(inv.vat_amount, inv.currency)}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {formatCurrency(inv.invoice_total, inv.currency)}
                          </td>
                          <td>{inv.currency || ''}</td>
                          <td>
                            {perInvoicePackages ? (
                              renderBatchStatusCell(inv.batch)
                            ) : (
                              <BatchStatusBadge status={inv.batch_status} />
                            )}
                          </td>
                          <td>
                            {/* A row with neither a package nor an open
                                invoice (below threshold, still extracting,
                                already submitted elsewhere) has nothing to
                                act on — render no control at all rather than
                                a permanently disabled Generate button on
                                every such row. */}
                            {perInvoicePackages && (inv.batch || inv.status === 'vat_open')
                              ? renderBatchActions(inv.batch, {
                                  canGenerate: inv.status === 'vat_open',
                                  onGenerate: () =>
                                    openGenerateModal(
                                      period.period,
                                      vendor.vendor_id,
                                      inv.batch ? inv.batch.id : null
                                    ),
                                  generateTitle:
                                    inv.status === 'vat_open'
                                      ? 'Generate the VAT package for this invoice'
                                      : 'Only an open invoice can be packaged',
                                })
                              : null}
                          </td>
                        </tr>
                      ))}
                      <tr className="vat-subtotal-row">
                        <td colSpan={3} style={{ textAlign: 'right', fontWeight: 600 }}>
                          {vendor.vendor_name || '—'} subtotal
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatCurrency(vendor.subtotal_service, vendor.currency)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatCurrency(vendor.subtotal_vat, vendor.currency)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatCurrency(vendor.subtotal_invoice_total, vendor.currency)}
                        </td>
                        <td>{vendor.currency || ''}</td>
                        <td>
                          {perInvoicePackages ? null : renderBatchStatusCell(batch)}
                        </td>
                        <td>
                          {perInvoicePackages
                            ? null
                            : renderBatchActions(batch, {
                                canGenerate: hasOpen,
                                onGenerate: () =>
                                  openGenerateModal(period.period, vendor.vendor_id),
                                generateTitle: hasOpen
                                  ? 'Generate VAT package for this vendor group'
                                  : 'No open invoices to batch',
                              })}
                        </td>
                      </tr>
                      {perInvoicePackages
                        ? vendor.invoices
                            .filter(
                              (inv) => inv.batch && expandedBatches.has(inv.batch.id)
                            )
                            .map((inv) => renderChecklistRow(inv.batch))
                        : batch &&
                          expandedBatches.has(batch.id) &&
                          renderChecklistRow(batch)}
                    </React.Fragment>
                  );
                })}
                <tr className="vat-period-total-row">
                  <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>
                    {period.period} totals
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {formatCurrency(period.total_service, period.currency)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {formatCurrency(period.total_vat, period.currency)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {formatCurrency(period.total_invoice_total, period.currency)}
                  </td>
                  <td>{period.currency || ''}</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
            )}
          </div>
          );
        })
      )}

      <RequestorApproverModal
        open={!!packageModal}
        title={packageModal?.mode === 'regenerate' ? 'Regenerate VAT Package' : 'Generate VAT Package'}
        confirmLabel={packageModal?.mode === 'regenerate' ? 'Regenerate Package' : 'Generate Package'}
        defaultRequestor={DEFAULT_REQUESTOR}
        initialRequestor={modalRequestor}
        initialApprover={modalApprover}
        fxOptions={modalFxOptions}
        payingBanks={modalPayingBanks}
        initialPayingBankId={modalPayingBankId}
        companyCodes={modalCompanyCodes}
        initialCompanyCodeId={modalCompanyCodeId}
        busy={modalBusy}
        onConfirm={handleConfirmPackage}
        onClose={closePackageModal}
      />
    </div>
  );
}

export default VatDashboard;
